import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import DashboardLayout from "../../layouts/DashboardLayout";
import api from "../../api";
import { Card } from "../../components/ui/CCard";
import {
  AlertTriangle, Loader2, Building2, CheckCircle2, RefreshCw, ChevronDown,
} from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const STATUS_OPTIONS = ["Initiated", "Processing", "Completed", "Failed"];
const STATUS_BADGE = {
  Initiated: { bg: "bg-blue-100", text: "text-blue-700" },
  Processing: { bg: "bg-amber-100", text: "text-amber-700" },
  Completed: { bg: "bg-green-100", text: "text-green-700" },
  Failed: { bg: "bg-red-100", text: "text-red-700" },
};

function BatchRow({ batch, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(batch.status);
  const [reference, setReference] = useState(batch.transaction_reference || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.updatePayoutBatch(batch.id, { status, transaction_reference: reference });
      onUpdated(res.data);
      setEditing(false);
      toast.success(`Batch #${batch.id} updated`);
    } catch (err) {
      // toast handled by global interceptor
    } finally {
      setSaving(false);
    }
  };

  const badge = STATUS_BADGE[batch.status] || STATUS_BADGE.Initiated;

  return (
    <tr className="hover:bg-secondary-50/30 transition-colors align-top">
      <td className="px-4 py-3 font-mono text-secondary-600">#{batch.id}</td>
      <td className="px-4 py-3 font-medium text-secondary-900">{batch.org_name || batch.org_id}</td>
      <td className="px-4 py-3 text-secondary-600">{new Date(batch.initiated_at).toLocaleDateString()}</td>
      <td className="px-4 py-3 text-right font-semibold text-secondary-900">{money(batch.total_amount)}</td>
      <td className="px-4 py-3">
        {editing ? (
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border border-secondary-200 rounded-lg px-2 py-1">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge.bg} ${badge.text}`}>{batch.status}</span>
        )}
      </td>
      <td className="px-4 py-3 text-secondary-500 font-mono text-xs">
        {editing ? (
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Bank/RazorpayX reference"
            className="text-xs border border-secondary-200 rounded-lg px-2 py-1 w-40"
          />
        ) : (
          batch.transaction_reference || "—"
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex justify-end gap-1.5">
            <button onClick={() => setEditing(false)} className="text-xs text-secondary-500 px-2 py-1">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg px-2.5 py-1 disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
            </button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-xs font-medium text-primary-600 hover:underline">Update</button>
        )}
      </td>
    </tr>
  );
}

export default function PayoutsAdmin() {
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeTab, setActiveTab] = useState("initiate");

  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  const [eligibleRows, setEligibleRows] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const res = await api.getPayoutOrgsOverview();
      setOrgs(res.data.orgs || []);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const fetchEligible = useCallback(async () => {
    if (!selectedOrgId) return;
    setEligibleLoading(true);
    setSelectedIds(new Set());
    try {
      const res = await api.getEligiblePayoutConsultations({ org_id: selectedOrgId });
      setEligibleRows(res.data.rows || []);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    } finally {
      setEligibleLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => { fetchEligible(); }, [fetchEligible]);

  const fetchBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const res = await api.getPayoutBatches(selectedOrgId ? { org_id: selectedOrgId } : {});
      setBatches(res.data.batches || []);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    } finally {
      setBatchesLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (activeTab === "history") fetchBatches();
  }, [activeTab, fetchBatches]);

  const toggleSelected = (appointmentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(appointmentId)) next.delete(appointmentId);
      else next.add(appointmentId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === eligibleRows.length ? new Set() : new Set(eligibleRows.map((r) => r.appointment_id))
    );
  };

  const selectedTotal = eligibleRows
    .filter((r) => selectedIds.has(r.appointment_id))
    // Payable = net minus per-session AI fees (training BE ai_billing.py);
    // the backend re-calculates fees for exactly the selected lines.
    .reduce((sum, r) => sum + (r.payable_amount ?? r.net_amount), 0);

  const handleInitiate = async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      const res = await api.createPayoutBatch({
        org_id: selectedOrgId,
        appointment_ids: Array.from(selectedIds),
        notes: notes || undefined,
      });
      toast.success(`Payout batch #${res.data.id} initiated for ${money(res.data.total_amount)}`);
      setNotes("");
      fetchEligible();
      fetchOrgs();
    } catch (err) {
      // toast handled by global interceptor
    } finally {
      setSubmitting(false);
    }
  };

  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4 text-center">
          <AlertTriangle size={48} className="text-rose-400" />
          <h2 className="text-xl font-bold text-secondary-800">Access Restricted</h2>
          <p className="text-secondary-500 max-w-md">Payouts are only accessible to users belonging to the admin organisation.</p>
        </div>
      </DashboardLayout>
    );
  }

  const selectedOrg = orgs.find((o) => o.org_id === selectedOrgId);

  return (
    <DashboardLayout>
      <div className="px-4 py-6 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-secondary-800">Org Payouts</h1>
          <p className="text-sm text-secondary-400 mt-0.5">
            Review completed, online-paid consultations and initiate payouts to each organisation.
          </p>
        </div>

        <div className="border-b border-secondary-200">
          <nav className="flex gap-6">
            <button
              onClick={() => setActiveTab("initiate")}
              className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === "initiate" ? "border-primary-600 text-primary-600" : "border-transparent text-secondary-500 hover:text-secondary-800"}`}
            >
              Initiate Payout
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === "history" ? "border-primary-600 text-primary-600" : "border-transparent text-secondary-500 hover:text-secondary-800"}`}
            >
              Batch History
            </button>
          </nav>
        </div>

        {activeTab === "initiate" ? (
          <>
            {/* Org picker */}
            <Card className="p-4">
              <div className="flex items-center gap-2 text-secondary-400 text-sm font-medium mb-3">
                <Building2 size={15} /> Select Organisation
              </div>
              {orgsLoading ? (
                <div className="flex items-center gap-2 text-secondary-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading orgs...</div>
              ) : orgs.length === 0 ? (
                <p className="text-sm text-secondary-400 italic">No organisations currently have unpaid-out consultations.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {orgs.map((o) => (
                    <button
                      key={o.org_id}
                      onClick={() => setSelectedOrgId(o.org_id)}
                      className={`px-3 py-2 rounded-xl text-sm border transition-colors ${selectedOrgId === o.org_id ? "bg-primary-500 text-white border-primary-500" : "bg-white border-secondary-200 text-secondary-700 hover:border-primary-300"}`}
                    >
                      {o.org_name} <span className="opacity-70">· {money(o.amount)} ({o.count})</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {selectedOrgId && (
              <>
                <Card className="overflow-hidden">
                  {eligibleLoading ? (
                    <div className="flex items-center justify-center h-32 gap-2 text-secondary-400">
                      <Loader2 size={18} className="animate-spin" /> Loading eligible consultations...
                    </div>
                  ) : eligibleRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 gap-2 text-secondary-400">
                      <CheckCircle2 size={24} className="text-secondary-200" />
                      <p className="text-sm">Nothing eligible for {selectedOrg?.org_name || "this org"} right now.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="bg-secondary-50/50 border-b border-secondary-100 text-xs uppercase tracking-wider text-secondary-500 font-semibold">
                            <th className="px-4 py-3">
                              <input type="checkbox" checked={selectedIds.size === eligibleRows.length} onChange={toggleSelectAll} />
                            </th>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Patient</th>
                            <th className="px-4 py-3">Doctor</th>
                            <th className="px-4 py-3 text-right">Fee</th>
                            <th className="px-4 py-3 text-right">Net</th>
                            <th className="px-4 py-3 text-right">AI fee</th>
                            <th className="px-4 py-3 text-right">Payout</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary-100">
                          {eligibleRows.map((r) => (
                            <tr key={r.appointment_id} className="hover:bg-secondary-50/30 transition-colors">
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(r.appointment_id)}
                                  onChange={() => toggleSelected(r.appointment_id)}
                                />
                              </td>
                              <td className="px-4 py-3 text-secondary-500">{r.date}</td>
                              <td className="px-4 py-3 font-medium text-secondary-900">{r.patient_name || "Unknown"}</td>
                              <td className="px-4 py-3 text-secondary-600">{r.doctor_name || "Unknown"}</td>
                              <td className="px-4 py-3 text-right text-secondary-600">{money(r.fee)}</td>
                              <td className="px-4 py-3 text-right text-secondary-600">{money(r.net_amount)}</td>
                              <td className="px-4 py-3 text-right text-secondary-600">{r.ai_fee ? `- ${money(r.ai_fee)}` : "-"}</td>
                              <td className="px-4 py-3 text-right font-semibold text-secondary-900">{money(r.payable_amount ?? r.net_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {eligibleRows.length > 0 && (
                  <Card className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notes (optional)"
                        className="text-sm border border-secondary-200 rounded-xl px-3 py-2 flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-secondary-500">
                        {selectedIds.size} selected · <span className="font-semibold text-secondary-900">{money(selectedTotal)}</span>
                        {eligibleRows.some((r) => r.ai_fee) && (
                          <span className="block text-xs text-secondary-400">After per-session AI fees; final fees are worked out for the selected lines when the batch is created.</span>
                        )}
                      </span>
                      <button
                        onClick={handleInitiate}
                        disabled={selectedIds.size === 0 || submitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-colors disabled:opacity-50"
                      >
                        {submitting ? <Loader2 size={14} className="animate-spin inline" /> : `Initiate Payout`}
                      </button>
                    </div>
                  </Card>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChevronDown size={14} className="text-secondary-400" />
                <select
                  value={selectedOrgId || ""}
                  onChange={(e) => setSelectedOrgId(e.target.value ? Number(e.target.value) : null)}
                  className="text-sm border border-secondary-200 rounded-xl px-3 py-2"
                >
                  <option value="">All organisations</option>
                  {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.org_name}</option>)}
                </select>
              </div>
              <button
                onClick={fetchBatches}
                disabled={batchesLoading}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors disabled:opacity-50"
              >
                <RefreshCw size={15} className={batchesLoading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            <Card className="overflow-hidden">
              {batchesLoading ? (
                <div className="flex items-center justify-center h-32 gap-2 text-secondary-400">
                  <Loader2 size={18} className="animate-spin" /> Loading batches...
                </div>
              ) : batches.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-secondary-400 text-sm">No payout batches yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-secondary-50/50 border-b border-secondary-100 text-xs uppercase tracking-wider text-secondary-500 font-semibold">
                        <th className="px-4 py-3">Batch</th>
                        <th className="px-4 py-3">Org</th>
                        <th className="px-4 py-3">Initiated</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Reference</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {batches.map((b) => (
                        <BatchRow
                          key={b.id}
                          batch={b}
                          onUpdated={(updated) => setBatches((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
